<template>
    <div
        v-if="shouldShow"
        class="flex flex-col items-start self-stretch pt-6 mb-6"
    >
        <!-- Title + Collapse Toggle -->
        <button
            type="button"
            class="flex w-full pb-2 justify-between items-center cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 -mx-2 px-2 rounded transition-colors duration-150"
            @click="expanded = !expanded"
        >
            <h4 class="text-slate-800 dark:text-slate-100 text-base font-medium">
                Points by Card
            </h4>
            <Icon
                :icon="expanded ? 'chevron-up' : 'chevron-down'"
                class="w-5 h-5 text-slate-400"
            />
        </button>

        <!-- Card Breakdown List -->
        <div
            v-if="expanded"
            class="flex flex-col items-start self-stretch"
        >
            <div
                v-for="(card, index) in cards"
                :key="card.id"
                class="flex py-4 items-center gap-4 self-stretch border-b border-slate-200 dark:border-slate-700"
            >
                <!-- Card Art -->
                <div
                    class="flex w-14 h-9 justify-center items-center shrink-0"
                >
                    <img
                        v-if="card.card_image"
                        :src="`${cdnUrl}/${card.card_image.path}/_small/${card.card_image.file_name}`"
                        :alt="card.card_title || card.name"
                        class="max-w-full max-h-full object-contain rounded"
                    />
                    <div
                        v-else
                        class="w-14 h-9 bg-slate-200 dark:bg-slate-700 rounded flex items-center justify-center"
                    >
                        <Icon
                            icon="credit-card"
                            :width="20"
                            :height="20"
                            class="text-slate-400"
                        />
                    </div>
                </div>

                <!-- Card Name + Last 4 -->
                <div class="flex items-baseline gap-1 flex-1 min-w-0">
                    <span
                        class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5 truncate"
                    >
                        {{ card.card_title || card.name }}
                    </span>
                    <span
                        v-if="card.last_four"
                        class="text-xs text-slate-500 dark:text-slate-400 shrink-0 whitespace-nowrap"
                    >
                        ({{ card.last_four }})
                    </span>
                </div>

                <!-- Balance -->
                <div class="shrink-0 text-right">
                    <span
                        class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                    >
                        {{
                            card.balance
                                ? $filters.formatThousand(card.balance)
                                : '0'
                        }}
                    </span>
                </div>
            </div>

            <!-- Total Row -->
            <div class="flex py-4 items-center gap-4 self-stretch">
                <!-- Program Logo -->
                <div
                    class="flex w-14 h-9 justify-center items-center shrink-0"
                >
                    <img
                        v-if="program.image"
                        :src="getProgramImageUrl(program.image)"
                        :alt="program.name"
                        class="max-w-full max-h-full object-contain"
                    />
                </div>

                <!-- Total Label -->
                <div class="flex flex-col items-start flex-1 min-w-0">
                    <span
                        class="self-stretch text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                    >
                        Total
                    </span>
                </div>

                <!-- Total Balance -->
                <div class="shrink-0 text-right">
                    <span
                        class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                    >
                        {{ $filters.formatThousand(total) }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    program: {
        type: Object,
        required: true,
    },
    cards: {
        type: Array,
        required: true,
    },
    total: {
        type: Number,
        required: true,
    },
    shouldShow: {
        type: Boolean,
        required: true,
    },
    getProgramImageUrl: {
        type: Function,
        required: true,
    },
});

const expanded = ref(true);

const cdnUrl = computed(() => window.cdnUrl);
</script>
